import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
  FormControl,
  Stack,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import { ProjectDesignerRoleTags } from '@nohara-dxdev/mt-common';
import { useTranslation } from 'react-i18next';

import { CsvParseError } from '@/features/csv-upload/utils/csv-result-transformer';
import { useSettingValues } from '@/features/settings';

import { CreateItemTemplateDto } from '../api';
import { TemplateRoleConflict } from '../utils/item-template-csv-parser';
import { getRoleTagTranslationKey } from '../utils/project-role-tags';

// Updated interface for single role tag selection per conflict
export interface RoleConflictResolution {
  templateIndex: number;
  csvRoleName: string;
  selectedRoleTag: string | null;
}

// New interface for complete template role management using tags
export interface TemplateRoleState {
  templateIndex: number;
  resolvedRoleTags: string[]; // Auto-resolved role tags from CSV
  conflictResolutions: RoleConflictResolution[]; // User selections for conflicts
  removedConflicts: string[]; // CSV role names user chose to remove
  additionalRoleTags: string[]; // Manually added role tags
}

export interface ItemTemplatePreviewProps {
  templates: CreateItemTemplateDto[];
  roleConflicts?: TemplateRoleConflict[];
  roleStates?: TemplateRoleState[];
  onRoleStateChange?: (templateIndex: number, newState: TemplateRoleState) => void;
  errors?: CsvParseError[];
}

export const ItemTemplateCsvRowPreview = ({
  templates,
  roleConflicts = [],
  roleStates = [],
  onRoleStateChange,
  errors = [],
}: ItemTemplatePreviewProps) => {
  const { t } = useTranslation();
  const { getSettingEntityValue } = useSettingValues();

  // Get all available role tags as options
  const availableRoleTags = Object.values(ProjectDesignerRoleTags);

  // Initialize role states for templates that don't have them
  const getTemplateRoleState = (templateIndex: number): TemplateRoleState => {
    const existing = roleStates.find((state) => state.templateIndex === templateIndex);
    if (existing) return existing;

    // Create initial state from template data
    const template = templates[templateIndex];
    return {
      templateIndex,
      resolvedRoleTags: template.projectRoleTags ?? [],
      conflictResolutions: [],
      removedConflicts: [],
      additionalRoleTags: [],
    };
  };

  // Get all currently used role tags for a template (for filtering)
  const getUsedRoleTags = (templateIndex: number, excludeConflictRole?: string): string[] => {
    const state = getTemplateRoleState(templateIndex);
    const usedTags = [
      ...state.resolvedRoleTags,
      ...state.additionalRoleTags,
      ...state.conflictResolutions
        .filter((cr) => cr.csvRoleName !== excludeConflictRole && cr.selectedRoleTag)
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .map((cr) => cr.selectedRoleTag!),
    ];
    return usedTags;
  };

  // Get available role tags for selection (filtered to exclude already used)
  const getAvailableRoleTags = (
    templateIndex: number,
    excludeConflictRole?: string,
  ): ProjectDesignerRoleTags[] => {
    const usedTags = getUsedRoleTags(templateIndex, excludeConflictRole);
    return availableRoleTags.filter((tag) => !usedTags.includes(tag));
  };

  // Get all final role tags for a template
  const getFinalRoleTags = (templateIndex: number): string[] => {
    const state = getTemplateRoleState(templateIndex);
    const conflictRoleTags = state.conflictResolutions
      .filter((cr) => cr.selectedRoleTag)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      .map((cr) => cr.selectedRoleTag!);

    return [...state.resolvedRoleTags, ...conflictRoleTags, ...state.additionalRoleTags];
  };

  // Get role names from tags
  const getRoleTagNames = (roleTags: string[]) => {
    return roleTags.map((tag) => t(getRoleTagTranslationKey(tag)));
  };

  // Get conflicts for a specific template (excluding removed ones)
  const getActiveConflicts = (templateIndex: number) => {
    const templateConflicts =
      roleConflicts.find((c) => c.templateIndex === templateIndex)?.conflicts ?? [];
    const state = getTemplateRoleState(templateIndex);
    return templateConflicts.filter(
      (conflict) => !state.removedConflicts.includes(conflict.csvRoleName),
    );
  };

  // Update template role state
  const updateTemplateState = (templateIndex: number, updates: Partial<TemplateRoleState>) => {
    const currentState = getTemplateRoleState(templateIndex);
    const newState = { ...currentState, ...updates };
    onRoleStateChange?.(templateIndex, newState);
  };

  // Handle conflict resolution (single role tag selection)
  const handleConflictResolve = (
    templateIndex: number,
    csvRoleName: string,
    roleTag: string | null,
  ) => {
    const state = getTemplateRoleState(templateIndex);
    const existingResolutions = state.conflictResolutions.filter(
      (cr) => cr.csvRoleName !== csvRoleName,
    );
    const newResolutions = roleTag
      ? [...existingResolutions, { templateIndex, csvRoleName, selectedRoleTag: roleTag }]
      : existingResolutions;

    updateTemplateState(templateIndex, {
      conflictResolutions: newResolutions,
    });
  };

  // Handle removing a conflict entirely
  const handleRemoveConflict = (templateIndex: number, csvRoleName: string) => {
    const state = getTemplateRoleState(templateIndex);
    updateTemplateState(templateIndex, {
      removedConflicts: [...state.removedConflicts, csvRoleName],
      conflictResolutions: state.conflictResolutions.filter((cr) => cr.csvRoleName !== csvRoleName),
    });
  };

  // Handle removing a resolved role tag
  const handleRemoveRoleTag = (templateIndex: number, roleTag: string) => {
    const state = getTemplateRoleState(templateIndex);
    updateTemplateState(templateIndex, {
      resolvedRoleTags: state.resolvedRoleTags.filter((tag) => tag !== roleTag),
      additionalRoleTags: state.additionalRoleTags.filter((tag) => tag !== roleTag),
      conflictResolutions: state.conflictResolutions.filter((cr) => cr.selectedRoleTag !== roleTag),
    });
  };

  // Handle adding a new role tag
  const handleAddRoleTag = (templateIndex: number, roleTag: string) => {
    const state = getTemplateRoleState(templateIndex);
    updateTemplateState(templateIndex, {
      additionalRoleTags: [...state.additionalRoleTags, roleTag],
    });
  };

  // Check if template has unresolved conflicts
  const hasUnresolvedConflicts = (templateIndex: number) => {
    const activeConflicts = getActiveConflicts(templateIndex);
    const state = getTemplateRoleState(templateIndex);
    return activeConflicts.some(
      (conflict) =>
        !state.conflictResolutions.some(
          (cr) => cr.csvRoleName === conflict.csvRoleName && cr.selectedRoleTag,
        ),
    );
  };

  // Group errors by row
  const errorsByRow = errors.reduce<Record<number, CsvParseError[]>>((acc, error) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!acc[error.row]) {
      acc[error.row] = [];
    }
    acc[error.row].push(error);
    return acc;
  }, {});

  // Get unique error rows sorted
  const errorRows = Object.keys(errorsByRow)
    .map(Number)
    .sort((a, b) => a - b);

  if (templates.length === 0 && errors.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="textSecondary">
          {t('csvImport.noTemplates')}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Successful Templates Table */}
      {templates.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            {t('csvImport.successfulRows', 'Successfully Parsed Rows')} ({templates.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('itemTemplates.list.columns.name')}</TableCell>
                  <TableCell>{t('itemTemplates.list.columns.templateKey')}</TableCell>
                  <TableCell>{t('itemTemplates.list.columns.itemType')}</TableCell>
                  <TableCell>{t('itemTemplates.list.columns.constructionTypes')}</TableCell>
                  <TableCell>{t('itemTemplates.list.columns.isBoardItem')}</TableCell>
                  <TableCell>{t('itemTemplates.list.columns.canConnectToBoard')}</TableCell>
                  <TableCell>{t('itemTemplates.list.columns.projectRoles')}</TableCell>
                  <TableCell>
                    {t('itemTemplates.list.columns.infrastructureRequirements')}
                  </TableCell>
                  <TableCell>{t('itemTemplates.schemaDetail.attributes')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template, index) => {
                  const finalRoleTags = getFinalRoleTags(index);
                  const finalRoleNames = getRoleTagNames(finalRoleTags);
                  const activeConflicts = getActiveConflicts(index);
                  const hasConflicts = activeConflicts.length > 0;
                  const hasUnresolved = hasUnresolvedConflicts(index);
                  const availableForAdding = getAvailableRoleTags(index);

                  // Check if we should show the "Add Role" section
                  const state = getTemplateRoleState(index);
                  const assignedRolesCount =
                    state.resolvedRoleTags.length + state.additionalRoleTags.length;
                  const totalAssignedAndConflictCount = assignedRolesCount + activeConflicts.length;
                  const shouldShowAddRole =
                    totalAssignedAndConflictCount < availableRoleTags.length &&
                    availableForAdding.length > 0;

                  const attributeCount =
                    template.jsonSchema.properties &&
                    typeof template.jsonSchema.properties === 'object'
                      ? Object.keys(template.jsonSchema.properties).length
                      : 0;

                  // Count infrastructure requirements
                  const infrastructureRequirementCount =
                    template.infrastructureRequirements?.length ?? 0;

                  return (
                    <TableRow
                      key={index}
                      sx={{ backgroundColor: hasUnresolved ? 'warning.light' : 'inherit' }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {template.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{template.templateKey}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getSettingEntityValue<string>(template.itemTypeId)}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        {template.constructionTypeIds.length > 0 ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {template.constructionTypeIds.map((id, typeIndex) => (
                              <Chip
                                key={typeIndex}
                                label={getSettingEntityValue<string>(id)}
                                size="small"
                                variant="outlined"
                                color="secondary"
                                sx={{ mb: 0.5 }}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={template.isBoardItem ? t('common.yes') : t('common.no')}
                          size="small"
                          color={template.isBoardItem ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={template.canConnectToBoard ? t('common.yes') : t('common.no')}
                          size="small"
                          color={template.canConnectToBoard ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 300 }}>
                        <Stack spacing={2}>
                          {/* Show final resolved roles */}
                          {finalRoleNames.length > 0 && (
                            <Box>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ mb: 0.5, display: 'block' }}
                              >
                                {t('csvImport.assignedRoles')}:
                              </Typography>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                {finalRoleTags.map((roleTag, roleIndex) => {
                                  const roleName = t(getRoleTagTranslationKey(roleTag));
                                  return (
                                    <Chip
                                      key={roleIndex}
                                      label={roleName}
                                      size="small"
                                      variant="outlined"
                                      color="info"
                                      onDelete={() => {
                                        handleRemoveRoleTag(index, roleTag);
                                      }}
                                      deleteIcon={<CloseIcon />}
                                      sx={{ mb: 0.5 }}
                                    />
                                  );
                                })}
                              </Stack>
                            </Box>
                          )}

                          {/* Show role conflict selectors */}
                          {hasConflicts && (
                            <Box>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ mb: 0.5, display: 'block' }}
                              >
                                {t('csvImport.resolveConflicts')}:
                              </Typography>
                              <Stack spacing={1}>
                                {activeConflicts.map((conflict) => {
                                  const state = getTemplateRoleState(index);
                                  const resolution = state.conflictResolutions.find(
                                    (cr) => cr.csvRoleName === conflict.csvRoleName,
                                  );
                                  const selectedRoleTag = resolution?.selectedRoleTag ?? '';
                                  const availableOptions = conflict.availableTags.filter(
                                    (tag) =>
                                      !getUsedRoleTags(index, conflict.csvRoleName).includes(tag),
                                  );

                                  return (
                                    <Box
                                      key={conflict.csvRoleName}
                                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                    >
                                      <Box sx={{ flex: 1 }}>
                                        <Typography
                                          variant="caption"
                                          color="error"
                                          sx={{ mb: 0.5, display: 'block' }}
                                        >
                                          {t('csvImport.roleTagNotFound', {
                                            roleName: conflict.csvRoleName,
                                          })}
                                          {conflict.suggestion && (
                                            <Typography
                                              variant="caption"
                                              color="textSecondary"
                                              sx={{ ml: 1 }}
                                            >
                                              (Suggestion: {conflict.suggestion})
                                            </Typography>
                                          )}
                                        </Typography>
                                        <FormControl fullWidth size="small">
                                          <InputLabel>{t('csvImport.selectRole')}</InputLabel>
                                          <Select
                                            value={selectedRoleTag}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              handleConflictResolve(
                                                index,
                                                conflict.csvRoleName,
                                                value || null,
                                              );
                                            }}
                                            label={t('csvImport.selectRole')}
                                          >
                                            <MenuItem value="">
                                              <em>{t('csvImport.selectRole')}</em>
                                            </MenuItem>
                                            {availableOptions.map((tag) => (
                                              <MenuItem key={tag} value={tag}>
                                                {t(getRoleTagTranslationKey(tag))}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      </Box>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                          handleRemoveConflict(index, conflict.csvRoleName);
                                        }}
                                        title={t('csvImport.removeConflict')}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  );
                                })}
                              </Stack>
                            </Box>
                          )}

                          {/* Add role functionality - only show if there are unassigned roles available */}
                          {shouldShowAddRole && (
                            <Box>
                              <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>{t('csvImport.addRole')}</InputLabel>
                                <Select
                                  value=""
                                  onChange={(e) => {
                                    const roleTag = e.target.value as string;
                                    if (roleTag) {
                                      handleAddRoleTag(index, roleTag);
                                    }
                                  }}
                                  label={t('csvImport.addRole')}
                                  startAdornment={<AddIcon fontSize="small" sx={{ mr: 1 }} />}
                                >
                                  <MenuItem value="">
                                    <em>{t('csvImport.selectRoleToAdd')}</em>
                                  </MenuItem>
                                  {availableForAdding.map((tag) => (
                                    <MenuItem key={tag} value={tag}>
                                      {t(getRoleTagTranslationKey(tag))}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          )}

                          {/* Show message if no roles */}
                          {finalRoleNames.length === 0 && !hasConflicts && (
                            <Typography variant="body2" color="textSecondary">
                              {t('csvImport.noRoles')}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {infrastructureRequirementCount > 0
                            ? `${infrastructureRequirementCount} requirement(s)`
                            : 'No requirements'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {attributeCount > 0 ? `${attributeCount} attribute(s)` : 'No attributes'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Error Rows Section */}
      {errors.length > 0 && (
        <Box>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon />
                <Typography variant="subtitle1" component="span">
                  {t('csvImport.failedRows', 'Failed Rows')} ({errorRows.length})
                </Typography>
              </Box>
            </AlertTitle>
            <Typography variant="body2">
              {t(
                'csvImport.failedRowsMessage',
                'The following rows could not be parsed due to validation errors. Please fix the errors in your CSV file and try again.',
              )}
            </Typography>
          </Alert>

          <Accordion defaultExpanded={templates.length === 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {t('csvImport.viewErrorDetails', 'View Error Details')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {errorRows.map((rowNumber) => {
                  const rowErrors = errorsByRow[rowNumber];
                  return (
                    <Paper key={rowNumber} variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={t('csvImport.row', 'Row {{number}}', { number: rowNumber })}
                            color="error"
                            size="small"
                          />
                          <Typography variant="body2" color="text.secondary">
                            {rowErrors.length === 1
                              ? t('csvImport.oneError', '1 error')
                              : t('csvImport.multipleErrors', '{{count}} errors', {
                                  count: rowErrors.length,
                                })}
                          </Typography>
                        </Box>
                        <Divider />
                        <Stack spacing={1}>
                          {rowErrors.map((error, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                gap: 1,
                                p: 1,
                                bgcolor: 'error.lighter',
                                borderRadius: 1,
                              }}
                            >
                              <ErrorIcon color="error" fontSize="small" sx={{ mt: 0.5 }} />
                              <Box sx={{ flex: 1 }}>
                                {
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{ fontWeight: 'bold', display: 'block' }}
                                  >
                                    {error.field
                                      ? `${t('csvImport.column', 'Column')}: ${error.field}`
                                      : typeof error.column === 'number'
                                        ? `${t('csvImport.columnIndex', 'Index')}: ${error.column}`
                                        : `${t('csvImport.column', 'Column')}: ${error.column}`}
                                  </Typography>
                                }
                                <Typography variant="body2" color="error.dark">
                                  {error.message}
                                </Typography>
                                {error.value !== undefined && (
                                  <Typography variant="caption" color="text.secondary">
                                    {t('csvImport.value', 'Value')}: {JSON.stringify(error.value)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Stack>
  );
};
